import { useEffect } from "react";

type Props = {
  active: boolean;
  isPresenter: boolean;
  presenterLabel: string | null;
  index: number;
  total: number;
  frameTitle?: string;
  onPrev: () => void;
  onNext: () => void;
  onStop: () => void;
};

export function BoardFramePresentBar({
  active,
  isPresenter,
  presenterLabel,
  index,
  total,
  frameTitle,
  onPrev,
  onNext,
  onStop,
}: Props) {
  // Свайп по экрану на телефоне — листать фреймы (только ведущий, один палец).
  useEffect(() => {
    if (!active || !isPresenter) return;
    let startX = 0;
    let startY = 0;
    let trackingId: number | null = null;
    let multiTouch = false;

    function onDown(ev: PointerEvent) {
      if (ev.pointerType !== "touch") return;
      if (trackingId !== null) {
        multiTouch = true;
        return;
      }
      const t = ev.target;
      if (
        t instanceof Element &&
        t.closest(
          ".wb-floating-tools, .wb-tool-options-popover, .wb-selection-toolbar, .wb-frame-present, .wb-board-toolbar, .wb-presentation-overlay, .wb-timer-card, .wb-calc-card, .wb-code-card"
        )
      ) {
        return;
      }
      trackingId = ev.pointerId;
      multiTouch = false;
      startX = ev.clientX;
      startY = ev.clientY;
    }

    function onUp(ev: PointerEvent) {
      if (ev.pointerType !== "touch" || trackingId === null) return;
      if (ev.pointerId !== trackingId) {
        multiTouch = true;
        return;
      }
      const wasMulti = multiTouch;
      trackingId = null;
      multiTouch = false;
      if (wasMulti) return;
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (Math.abs(dx) < 72 || Math.abs(dx) < Math.abs(dy) * 1.35) return;
      if (dx < 0) onNext();
      else onPrev();
    }

    window.addEventListener("pointerdown", onDown, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true });
    window.addEventListener("pointercancel", onUp, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [active, isPresenter, onNext, onPrev]);

  if (!active || total < 1) return null;

  return (
    <div className="wb-frame-present" role="status" aria-live="polite">
      <div className="wb-frame-present__pill">
        {isPresenter ? "Вы презентуете" : `Смотрите: ${presenterLabel || "ведущий"}`}
      </div>
      <div className="wb-frame-present__bar">
        {isPresenter ? (
          <button
            type="button"
            className="wb-frame-present__nav"
            onPointerDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => {
              ev.stopPropagation();
              onPrev();
            }}
            disabled={index <= 0}
          >
            ←
          </button>
        ) : null}
        <span className="wb-frame-present__meta">
          {index + 1} / {total}
          {frameTitle ? ` · ${frameTitle}` : ""}
        </span>
        {isPresenter ? (
          <button
            type="button"
            className="wb-frame-present__nav"
            onPointerDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => {
              ev.stopPropagation();
              onNext();
            }}
            disabled={index >= total - 1}
          >
            →
          </button>
        ) : null}
        {isPresenter ? (
          <button
            type="button"
            className="wb-frame-present__stop"
            onPointerDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => {
              ev.stopPropagation();
              onStop();
            }}
          >
            Стоп
          </button>
        ) : null}
      </div>
    </div>
  );
}
